import React, { useMemo } from 'react';

import Icon from 'carbon-react/lib/components/icon';

import { ExtendedForecastListItem } from '@futrli/views/processors/forecasts/forecastsList';
import { FormattedMessage } from '@futrli/localisation';
import { ListingPageRowActionItem } from '@futrli/ui-components/components/listing-page/listing-page-row/listing-page-row-actions/listing-page-row-actions';
import { usePredictFlags } from '@futrli/predict/hooks/usePredictFlags';

import { useForecastActions } from '../context/forecast-actions-context/forecast-actions-context';

interface UseForecastActionItemsParams {
  forecast: ExtendedForecastListItem;
}

export const useForecastActionItems = ({ forecast }: UseForecastActionItemsParams): ListingPageRowActionItem[] => {
  const {
    handleDeleteClick,
    handleDuplicateClick,
    handleEditClick,
    handleExcelExportClick,
    handleExportClick,
    handleLockClick,
    handlePublishClick,
    handleShareClick
  } = useForecastActions();

  const { hasHideExportToGoogleSheetsEnabled } = usePredictFlags();
  const showExportToGoogleSheets = forecast.actions.exportToGoogleSheets && !hasHideExportToGoogleSheetsEnabled;

  return useMemo<ListingPageRowActionItem[]>(() => {
    const items: ListingPageRowActionItem[] = [];
    const { actions } = forecast;

    if (actions.edit) {
      items.push({
        label: <FormattedMessage id="predict.forecasts.actions.edit_details" defaultMessage="Edit details" />,
        onClick: () => {
          handleEditClick(forecast);
        }
      });
    }

    if (actions.duplicate) {
      items.push({
        label: <FormattedMessage id="common.duplicate" defaultMessage="Duplicate" />,
        onClick: () => {
          handleDuplicateClick(forecast);
        }
      });
    }

    if (actions.lock) {
      items.push({
        label: <FormattedMessage id="common.lock" defaultMessage="Lock" />,
        onClick: () => {
          handleLockClick(forecast);
        }
      });
    }

    if (actions.publish) {
      items.push({
        label: <FormattedMessage id="common.publish" defaultMessage="Publish" />,
        onClick: () => {
          handlePublishClick(forecast);
        }
      });
    }

    if (actions.unpublish) {
      items.push({
        label: <FormattedMessage id="common.unpublish" defaultMessage="Unpublish" />,
        onClick: () => {
          handlePublishClick(forecast);
        }
      });
    }

    if (actions.share) {
      items.push({
        label: <FormattedMessage id="common.manage_access" defaultMessage="Manage access" />,
        onClick: () => {
          handleShareClick(forecast);
        }
      });
    }

    if (showExportToGoogleSheets) {
      items.push({
        label: (
          <FormattedMessage
            id="predict.forecasts.actions.export_google_sheets"
            defaultMessage="Export to Google Sheets"
          />
        ),
        onClick: () => {
          handleExportClick(forecast);
        }
      });
    }

    if (actions.exportToExcel) {
      items.push({
        label: (
          <FormattedMessage
            id="predict.export_to_excel"
            defaultMessage="Export to Excel"
          />
        ),
        onClick: () => {
          handleExcelExportClick(forecast);
        }
      });
    }

    if (actions.delete) {
      items.push({
        label: (
          <>
            <Icon type="delete" fontSize="small" />
            <FormattedMessage id="common.delete" defaultMessage="Delete" />
          </>
        ),
        onClick: () => {
          handleDeleteClick(forecast);
        },
        variant: 'danger'
      });
    }

    return items;
  }, [
    forecast,
    handleDeleteClick,
    handleDuplicateClick,
    handleEditClick,
    handleExcelExportClick,
    handleExportClick,
    handleLockClick,
    handlePublishClick,
    handleShareClick,
    showExportToGoogleSheets
  ]);
};
